import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Pencil, Trash2, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
}

interface CommentSectionProps {
  postId: string;
  comments: Comment[];
  onCommentUpdate: () => Promise<void>;
}

export default function CommentSection({ postId, comments, onCommentUpdate }: CommentSectionProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const { error: submitError } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: newComment.trim()
        });

      if (submitError) throw submitError;

      setNewComment('');
      await onCommentUpdate();
    } catch (error) {
      console.error('Error submitting comment:', error);
      setError('Failed to post comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent, commentId: string) => {
    e.preventDefault();
    if (!user || !editContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const { error: editError } = await supabase
        .from('comments')
        .update({ 
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (editError) throw editError;

      setEditingCommentId(null);
      setEditContent('');
      await onCommentUpdate();
    } catch (error) {
      console.error('Error editing comment:', error);
      setError('Failed to edit comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      await onCommentUpdate();
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError('Failed to delete comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
    setError(null);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditContent('');
    setError(null);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Comment form */}
      <form onSubmit={handleSubmitComment} className="flex gap-4">
        <div className="relative group flex-1">
          <div className="absolute inset-0 bg-royal-purple/5 rounded-xl blur-lg opacity-0 group-focus-within:opacity-100 transition-opacity" />
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 relative dark:bg-dark-elevated dark:text-dark-primary dark:placeholder-gray-500"
          />
        </div>
        <button
          type="submit"
          disabled={!newComment.trim() || isSubmitting}
          className={cn(
            'px-6 py-2 rounded-xl font-medium transition-all duration-300',
            !newComment.trim() || isSubmitting
              ? 'bg-gray-100 dark:bg-dark-elevated text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'bg-gradient-to-r from-royal-purple to-royal-blue text-white hover:shadow-lg hover:shadow-royal-purple/25 transform hover:-translate-y-0.5 active:translate-y-0'
          )}
        >
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
      </form>

      {/* Comments list */}
      <div className="space-y-4">
        {comments.map((comment) => (
          <div
            key={comment.id}
            className="bg-gray-50 dark:bg-dark-elevated rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-dark-paper transition-colors group"
          >
            <div className="flex items-start gap-3">
              <img
                src={comment.user?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${comment.user?.full_name}`}
                alt={comment.user?.full_name}
                className="w-8 h-8 rounded-full ring-2 ring-royal-purple/20"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-dark-primary">
                    {comment.user?.full_name}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(comment.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {comment.updated_at && comment.updated_at !== comment.created_at && (
                      <span className="ml-1 text-xs">(edited)</span>
                    )}
                  </p>
                </div>

                {editingCommentId === comment.id ? (
                  <form 
                    onSubmit={(e) => handleEditSubmit(e, comment.id)}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleEditSubmit(e, comment.id);
                        }
                        if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                      className="flex-1 px-3 py-1 rounded-lg border border-gray-200 dark:border-dark-border focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none dark:bg-dark-elevated dark:text-dark-primary"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={!editContent.trim() || isSubmitting}
                      className="p-2 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                      title="Save changes"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-paper transition-colors"
                      title="Cancel editing"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </form>
                ) : (
                  <p className="text-gray-700 dark:text-dark-primary">{comment.content}</p>
                )}
              </div>

              {user?.id === comment.user_id && !editingCommentId && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button
                    type="button"
                    onClick={() => startEditing(comment)}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-paper transition-colors"
                    title="Edit comment"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteComment(comment.id)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete comment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}